import { useState } from "react";
import { HelpCircle, AlertTriangle } from "lucide-react";
import CodeField from "@/components/form/CodeField";
import Modal from "@/components/ui/Modal";
import { ANYTHINK_TYPE_DEFINITIONS } from "./anythink-types";
import { Box, Text, IconButton, Code, Flex, Callout } from "@radix-ui/themes";
import { object, string, InferType } from "yup";

export const runScriptValidationSchema = object({
  parameters: object().shape({
    script: string().required("Script is required"),
  }),
});

export type RunScriptFormValues = InferType<typeof runScriptValidationSchema>;

const AnythinkHelpContent = () => {
  return (
    <Box p="4" style={{ maxHeight: "70vh", overflow: "auto" }}>
      <Text size="3" weight="medium" mb="3" style={{ display: "block" }}>
        Using the $anythink Object
      </Text>
      <Text size="2" color="gray" mb="4" style={{ display: "block" }}>
        The <Code>$anythink</Code> object provides access to workflow context,
        trigger data, and results from previous steps. It's automatically
        available in your script.
      </Text>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Workflow Information
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.workflow.id
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.workflow.name
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.workflow.description
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "12px" }}>
          $anythink.workflow.created_at
        </Code>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Access information about the current workflow execution.
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Current Job & Step
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.job.id
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.job.step.key
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.job.step.name
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.job.step.action
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "12px" }}>
          $anythink.job.step.parameters
        </Code>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Information about the current workflow job and step being executed.
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Trigger Data
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.trigger.id
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.trigger.entity_name
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "12px" }}>
          $anythink.trigger.data
        </Code>
        <Text size="1" color="gray" style={{ display: "block" }}>
          The payload that triggered this workflow (e.g., entity data from an
          event trigger).
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Previous Step Results
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          $anythink.steps["step-key"]
        </Code>
        <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
          Access data from previous workflow steps using the step key. Each
          step's output is stored under its step key.
        </Text>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Example: <Code>$anythink.steps["read-data-1"]</Code> contains the
          results from a step with key "read-data-1".
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Console Logging
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          console.log("Message", data)
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          console.info("Info message")
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          console.warn("Warning")
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "12px" }}>
          console.error("Error")
        </Code>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Use console methods to log messages that will appear in the workflow
          execution logs.
        </Text>
      </Box>

      <Box mb="4" mt="2">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Execution Limits & Best Practices
        </Text>
        <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
          To ensure reliability and prevent worker freezing, the execution of
          scripts is constrained:
        </Text>
        <ul
          style={{
            paddingLeft: "16px",
            margin: "4px 0 12px 0",
            listStyleType: "disc",
          }}
        >
          <li>
            <Text size="1" color="gray">
              <strong>Statement Limit:</strong> Maximum of 100,000 executed
              statements per run.
            </Text>
          </li>
          <li>
            <Text size="1" color="gray">
              <strong>Memory & Timeout:</strong> Maximum of 64MB memory and
              30-second duration.
            </Text>
          </li>
        </ul>
        <Text size="1" color="gray" style={{ display: "block" }}>
          <strong>Tip:</strong> Anythink counts every loop check and comparison
          as a statement. When processing large arrays, avoid nested searches
          (e.g., calling <Code size="1">.find()</Code> inside a{" "}
          <Code size="1">.forEach()</Code> loop) since they consume statements
          quadratically (O(N x M)). Map arrays to lookup objects first to keep
          statements minimal.
        </Text>
      </Box>

      <Box>
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Example Usage
        </Text>
        <Code
          size="1"
          style={{
            display: "block",
            padding: "12px",
            backgroundColor: "var(--gray-2)",
            borderRadius: "var(--radius-2)",
            fontFamily: "monospace",
            whiteSpace: "pre",
            overflow: "auto",
          }}
        >
          {`// Access trigger data
const entityData = $anythink.trigger.data;

// Get results from a previous step
const previousResults = $anythink.steps["read-data-1"];

// Log information
console.log("Processing", entityData);

// Return data for next steps
return {
  processed: true,
  count: previousResults?.data?.length || 0
};`}
        </Code>
      </Box>
    </Box>
  );
};

export default function RunScriptFields() {
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div className="space-y-6">
      <CodeField
        name="parameters.script"
        label="JavaScript"
        language="javascript"
        required
        typeDefinitions={ANYTHINK_TYPE_DEFINITIONS}
        isEditing={true}
        description={
          <Flex align="center" gap="2">
            <Text size="2" color="gray">
              Access workflow context via the <Code size="1">$anythink</Code>{" "}
              object
            </Text>
            <IconButton
              type="button"
              variant="ghost"
              size="1"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowHelp(true);
              }}
              title="Help: Using $anythink object"
              style={{ padding: "2px", cursor: "pointer" }}
            >
              <HelpCircle size={14} />
            </IconButton>
          </Flex>
        }
      />

      <Callout.Root size="1" color="amber">
        <Callout.Icon>
          <AlertTriangle size={14} />
        </Callout.Icon>
        <Callout.Text>
          Scripts have a safety limit of{" "}
          <strong>100,000 executed statements</strong> to prevent worker freeze.
          For large datasets, index items into lookup objects first to avoid
          nested <Code size="1">.find()</Code> or{" "}
          <Code size="1">.filter()</Code> iterations, or use native looping
          actions.
        </Callout.Text>
      </Callout.Root>

      <Modal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Script Help: $anythink Object"
        size="lg"
      >
        <AnythinkHelpContent />
      </Modal>
    </div>
  );
}
